import { Server } from 'socket.io';
import Container, { Service } from 'typedi';
import { IRole } from '../../Role/interfaces/role.interface';
import mongoose, { ClientSession, Types } from 'mongoose';
import { HttpException } from '../../../utils/exceptions/httpException';
import { status } from '../../../utils/helpers/api.responses';
import { aws } from '../../../config/index';
import { TFunction } from 'i18next';
import User from '../../../Modules/Auth/models/auth.model';
import { CreateNotificationParams, getNotificationMessages, INotification, NotificationAction, NotificationConfig, NotificationData, NotificationModule } from '../interfaces/notification.interface';
import NotificationModel from '../models/notification.model';
import { IAttachment } from '../../../Modules/Attachment/interfaces/attcahment.interface';
import { RolesEnum } from '../../../Modules/Auth/interfaces/auth.interface';
import { sendAllocationRequestDeletedEmail, sendAllocationRequestEmail, sendAllocationResponseEmail, sendAvailabilityCreatedEmail, sendAvailabilityDeletedEmail, sendAvailabilityUpdatedEmail, sendJobClosedEmail, sendJobHardDeletedEmail, sendLabourDeletedEmail, sendNewRatingEmail, sendProfileCompletedEmail, sendProfileUpdatedEmail, sendRatingDeletedEmail, sendRatingUpdatedEmail } from '../../../utils/mail/mailer';
import Job from '../../../Modules/jobManagement/models/job.model';


@Service()
export class NotificationService {
    private io: Server;

    public getIO(): Server {
        if (!this.io) {
            try {
                this.io = Container.get<Server>('io');
                console.log('Socket.IO initialized and set in container');
            } catch (error) {
                if (error instanceof HttpException) throw error;
                console.warn('Socket.IO not available yet, will retry on next emission');
            }
        }
        return this.io;
    }


    public async sendNotification<T extends NotificationModule>(
        module: T,
        action: NotificationAction<T>,
        data: Omit<NotificationData, 'eventType' | 'title' | 'type'>,
        t: TFunction
    ): Promise<void> {
        const { from, to, entityData } = data;
        const config = getNotificationMessages(t, module, action as string);
        if (!config) return;

        const recipientIds = Array.isArray(to) ? to : [to];
        console.log("recipientIds", recipientIds)
        for (const recipientId of recipientIds) {
            await this.sendSingleNotification({
                from,
                to: recipientId,
                entityData,
                config,
                t
            });
        }
    }

    private async sendSingleNotification({
        from,
        to,
        entityData,
        config,
        t }: {
            from?: Types.ObjectId,
            to: Types.ObjectId,
            entityData: any,
            config: NotificationConfig,
            t: TFunction
        }) {
        const { title, type, recipient, messages, module, action } = config;
        const recipientUser = await User.findById(to)
            .select('notificationPreferences firstName lastName fullName email profileImage roleId')
            .populate('roleId');

        const sender = from
            ? await User.findById(from)
                .select('notificationPreferences firstName lastName fullName email profileImage roleId')
                .populate('roleId')
            : null;

        const job = entityData.jobId
            ? await Job.findById(entityData.jobId)
                .select('companyName location')
                .lean()
            : null;


        if (!recipientUser) return;
        const recipientRole = recipientUser.roleId as IRole;
        const isAdmin = recipientRole.name.toLowerCase() === RolesEnum.ADMIN.toLowerCase();
        const isStaff = recipientRole.name.toLowerCase() === RolesEnum.STAFF.toLowerCase();


        let replacements = {
            '{username}': sender?.firstName ?? sender?.email ?? entityData.firstName ?? 'User',
            '{adminName}': sender?.firstName ?? 'Admin',
            '{staffName}': sender?.firstName ?? 'Staff',
            '{targetName}': recipientUser.firstName ?? 'User',
            '{updatedFields}': entityData?.updatedFields ?? '',
            '{location}': job?.location ?? entityData?.location ?? 'Unknown',
            '{wage}': entityData?.wage ?? '',
            '{startDate}': entityData?.startDate ?? '',
            '{endDate}': entityData?.endDate ?? '',
            '{status}': entityData?.status ?? '',
            '{messageMessage}': entityData.message ? ` Message: ${entityData.message}` : '',
            '{responseNotesMessage}': entityData.responseNotes ? ` Response Notes: ${entityData.responseNotes}` : '',
            '{responderName}': entityData?.firstName ?? sender?.email ?? 'User',
            '{responseNotes}': entityData?.responseNotes ?? '',
            '{companyName}': job?.companyName ?? entityData?.companyName ?? 'Unknown',
            '{firstName}': entityData.firstName ?? recipientUser.firstName ?? '',
            '{lastName}': entityData.lastName ?? recipientUser.lastName ?? '',
            '{rating}': entityData.rating?.toString() ?? '',
            '{feedback}': entityData.feedback ?? 'None',
            '{raterName}': entityData.raterName ?? 'Unknown',
            '{feedbackMessage}': entityData.feedback ? ` Feedback: ${entityData.feedback}` : '',

            '{holidayNotesMessage}': entityData.holidayNotes ? `\nHoliday Notes: ${entityData.holidayNotes}` : '',
            '{labourId}': entityData.labourId ?? 'N/A',
        };




        let roleBasedMessage: string | undefined;

        if (isAdmin && messages.admin) {
            roleBasedMessage = messages.admin;
        } else if (isStaff && messages.staff) {
            roleBasedMessage = messages.staff;
        } else if (messages.user) {
            roleBasedMessage = messages.user;
        }



        let message = roleBasedMessage;
        for (const [key, value] of Object.entries(replacements)) {
            message = message?.replace(key, value);
        }
        console.log("message", message)
        const recipientPrefs = recipientUser.notificationPreferences ?? ['inApp'];
        if (recipientPrefs.includes('inApp') && message) {
            await this.createNotification(t, {
                from,
                to,
                title,
                message,
                type,
                result: entityData.result ?? 'PENDING',
                status: entityData.status,
                fullName: recipientUser.fullName,
                profileImage: sender?.profileImage?._id,
                roleName: recipientRole.name.toLowerCase(),
            },
            );

        }


        if (recipientPrefs.includes('email') && message) {
            console.log(`📧 Email sent to: ${recipientUser.email} | Message: ${message}`);
            const labourerName = `${entityData.firstName} ${entityData.lastName}`.trim()
            const userName = labourerName || recipientUser.lastName || recipientUser.firstName;
            console.log("labourerName", labourerName)
            if (module === 'labourManagement') {
                switch (action) {
                    case 'profileCompleted':
                        await sendProfileCompletedEmail(
                            recipientUser.email,
                            title,
                            message,
                            labourerName,
                            entityData.email,
                            entityData.labourId
                        )
                        break;
                    case 'labourProfileUpdated':
                        await sendProfileUpdatedEmail(
                            recipientUser.email,
                            title,
                            message,
                            labourerName,
                            entityData.updatedFields,
                            entityData.labourId
                        )
                        break;
                    case 'labourDeletedByStaff':
                        await sendLabourDeletedEmail(
                            recipientUser.email,
                            title,
                            message,
                            labourerName,
                            entityData.email
                        )
                        break;
                    case 'newRatingReceived':
                        await sendNewRatingEmail(
                            recipientUser.email,
                            title,
                            message,
                            labourerName,
                            entityData.rating,
                            entityData.feedback || 'None',
                            entityData.raterName,
                            entityData.labourId
                        );
                        break;
                    case 'ratingUpdated':
                        await sendRatingUpdatedEmail(
                            recipientUser.email,
                            title,
                            message,
                            userName,
                            entityData.rating,
                            entityData.feedback || 'None',
                            entityData.adminName,
                            entityData.updatedFields,
                            entityData.labourId
                        );
                        break;
                    case 'ratingDeleted':
                        await sendRatingDeletedEmail(
                            recipientUser.email,
                            title,
                            message,
                            userName,
                            entityData.adminName,
                            entityData.labourId
                        );
                        break;
                }
            } else if (module === 'userManagement') {
                switch (action) {
                    case 'userRightsRevoked':
                        break;
                }
            } else if (module === 'allocationRequest') {
                switch (action) {
                    case 'newAllocationRequest':
                        await sendAllocationRequestEmail(
                            recipientUser.email,
                            `${process.env.APP_URL}/allocation-requests/respond/${entityData.allocationRequestId}`,
                            userName,
                            message,
                            entityData.location,
                            entityData.wage,
                            entityData.startDate,
                            entityData.endDate,
                            entityData.labourId,
                            entityData.companyName
                        );
                        break;
                    case 'respondAllocationRequest':
                        await sendAllocationResponseEmail(
                            recipientUser.email,
                            userName,
                            labourerName,
                            entityData.labourId,
                            entityData.allocationRequestId,
                            entityData.status,
                            entityData.responseNotes,
                            entityData.startDate,
                            entityData.endDate,
                            entityData.location,
                            entityData.wage,
                            `${process.env.APP_URL}/allocation-requests/${entityData.allocationRequestId}`,
                            entityData.status.toLowerCase() === 'accepted' ? '#28a745' : '#dc3545',
                            entityData.companyName ?? 'N/A',
                        );
                        break;
                    case 'deleteAllocationRequest':
                        await sendAllocationRequestDeletedEmail(
                            recipientUser.email,
                            title,
                            message,
                            userName,
                            entityData.labourId,
                            entityData.startDate,
                            entityData.endDate,
                            entityData.location,
                            entityData.wage,
                            entityData.responseNotes,
                            entityData.companyName ?? 'N/A',
                        );
                        break;

                }
            } else if (module === 'availability') {
                switch (action) {
                    case 'newAvailability':
                        await sendAvailabilityCreatedEmail(
                            recipientUser.email,
                            title,
                            message,
                            userName,
                            entityData.startDate,
                            entityData.endDate,
                            entityData.location,
                            entityData.companyName,
                            entityData.status,
                            entityData.labourId
                        );
                        break;
                    case 'updateAvailability':
                        await sendAvailabilityUpdatedEmail(
                            recipientUser.email,
                            title,
                            message,
                            userName,
                            entityData.startDate,
                            entityData.endDate,
                            entityData.location,
                            entityData.companyName,
                            entityData.status,
                            entityData.updatedFields,
                            entityData.labourId
                        );
                        break;
                    case 'deleteAvailability':
                        await sendAvailabilityDeletedEmail(
                            recipientUser.email,
                            title,
                            message,
                            userName,
                            entityData.startDate,
                            entityData.endDate,
                            entityData.location,
                            entityData.companyName,
                            entityData.status,
                            entityData.labourId
                        );
                        break;
                }
            } else if (module === 'jobManagement') {
                switch (action) {
                    case 'jobHardDeleted':
                        await sendJobHardDeletedEmail(
                            recipientUser.email,
                            title,
                            message,
                            entityData.companyName,
                            entityData.location,

                        );
                        break;
                    case 'jobHardDeletedByStaff':
                        await sendJobHardDeletedEmail(
                            recipientUser.email,
                            title,
                            message,
                            entityData.companyName,
                            entityData.location
                        );
                        break;
                    case 'jobClosedPendingRequest': 
                        await sendJobClosedEmail(
                            recipientUser.email,
                            'The job has been closed, canceling your pending allocation request.',
                            message,
                            userName,
                            entityData.startDate,
                            entityData.endDate,
                            entityData.location,
                            entityData.companyName ?? 'N/A',
                            entityData.wage,
                        );
                        break;
                }
            }

        }

    }

    public async createNotification(
        t: TFunction,
        {
            from,
            to,
            title,
            message,
            type,
            result = 'PENDING',
            status,
            fullName,
            profileImage,
            roleName,
        }: CreateNotificationParams, session?: ClientSession): Promise<INotification> {
        const notification = await NotificationModel.create({
            from,
            to,
            title,
            message,
            type,
            isRead: false,
            result,
            fullName,
            profileImage,
        });
        console.log("message from notifications", notification.message)


        const populatedNotification = await NotificationModel.findById(notification._id)
            .populate<{ profileImage: IAttachment }>('profileImage')
            .session(session) // ✅ use session here too
            .lean();
        const responseNotification = {
            ...populatedNotification,
            profileImage: populatedNotification?.profileImage?._id
                ? {
                    _id: populatedNotification.profileImage._id.toString(),
                    filename: populatedNotification.profileImage.filename ?? '',
                    url: `${aws.S3_BASE_URL}/${populatedNotification.profileImage.key}`,
                }
                : null,
        };

        const io = this.getIO();
        const room = `${roleName.toLowerCase().replace(/\s+/g, '')}_${to}`;

        io.to(room).emit('new-notification', responseNotification);
        return responseNotification as INotification;
    }

    public async getUserNotifications(
        t: TFunction,
        userId: string,
        page: number = 1,
        limit: number = 10,
        search?: string,

    ) {
        const skip = (page - 1) * limit;

        const user = await User.findById(userId).populate<{ roleId: IRole }>('roleId');
        if (!user) {
            throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
        }

        const searchFilter = search
            ? { fullName: { $regex: search, $options: 'i' } }
            : {};

        let baseFilter: any = {
            isDeleted: false,
            to: user._id,
            ...searchFilter,
        };

        const total = await NotificationModel.countDocuments(baseFilter);

        const notifications = await NotificationModel.find(baseFilter)
            .sort({ createdAt: -1 })
            .skip(skip)
            .limit(limit)
            .populate('profileImage')
            .lean()
            .then((notifications) =>
                notifications.map((notification) => {
                    const profileImage = notification.profileImage && 'key' in notification.profileImage
                        ? {
                            _id: notification.profileImage._id,
                            filename: notification.profileImage.filename,
                            url: `${aws.S3_BASE_URL}/${notification.profileImage.key}`,
                        }
                        : null;

                    return {
                        ...notification,
                        profileImage,
                    };
                })
            );

        return {
            notifications,
            total,
            page,
            totalPages: Math.ceil(total / limit),
        };
    }

    public async markAsRead(t: TFunction, notificationId: string): Promise<INotification | null> {
        const notification = await NotificationModel.findByIdAndUpdate(
            notificationId,
            { isRead: true },
            { new: true }
        );

        if (notification) {
            const io = this.getIO();
            const user = await User.findById(notification.to).populate<{ roleId: IRole }>('roleId');
            if (user?.roleId?.name) {
                const roleRoom = `${user.roleId.name.toLowerCase().replace(/\s+/g, '')}_${user._id}`;
                io.to(roleRoom).emit('notification-updated', notification);
            }

        }

        return notification;
    }

    public async markAsUnread(t: TFunction, notificationId: string): Promise<INotification | null> {
        const notification = await NotificationModel.findByIdAndUpdate(
            notificationId,
            { isRead: false },
            { new: true }
        );

        if (notification) {
            const io = this.getIO();
            const user = await User.findById(notification.to).populate<{ roleId: IRole }>('roleId');
            if (user?.roleId?.name) {
                const roleRoom = `${user.roleId.name.toLowerCase().replace(/\s+/g, '')}_${user._id}`;
                io.to(roleRoom).emit('notification-updated', notification);
            }
        }

        return notification;
    }

    async markAllAsRead(userId: string): Promise<void> {
        await NotificationModel.updateMany(
            { to: userId, isRead: false },
            { isRead: true }
        );
        const io = this.getIO();
        const user = await User.findById(userId).populate<{ roleId: IRole }>('roleId');
        if (user?.roleId?.name) {
            const roleRoom = `${user.roleId.name.toLowerCase().replace(/\s+/g, '')}_${user._id}`;
            io.to(roleRoom).emit('all-notifications-read');
        }
    }

    public async deleteNotifications(
        t: TFunction,
        ids: string[],
        userId: string,
    ): Promise<any> {
        try {
            const objectIds = ids.map(id => new mongoose.Types.ObjectId(id));

            const [foundNotifications, deletionResult] = await Promise.all([
                NotificationModel.find({ _id: { $in: objectIds }, to: userId }).lean(),
                NotificationModel.deleteMany({ _id: { $in: objectIds }, to: userId })
            ]);

            if (foundNotifications.length === 0) {
                const notFoundMessage = `No notifications found with the provided IDs: ${ids.join(', ')}`;
                throw new HttpException(status.NotFound, notFoundMessage);
            }

            const notFoundIds = ids.filter(id => !foundNotifications.some(notif => notif._id.toString() === id));

            let notFoundMessage = '';
            if (notFoundIds.length > 0) {
                notFoundMessage = t('General.not_found', { field: t('Notification.notification') });
            }

            if (deletionResult.deletedCount === 0) {
                throw new HttpException(
                    status.NotFound,
                    t('General.not_found', { field: t('Notification.notification') }));
            }

            const io = this.getIO();
            const user = await User.findById(userId).populate<{ roleId: IRole }>('roleId');
            if (user?.roleId?.name) {
                const roleRoom = `${user.roleId.name.toLowerCase().replace(/\s+/g, '')}_${user._id}`;
                io.to(roleRoom).emit('notifications-deleted', { deletedIds: foundNotifications.map(n => n._id.toString()) });
            }

            return {
                deletedCount: deletionResult.deletedCount,
                notFoundCount: notFoundIds.length,
                notFoundMessage,
                deletedIds: foundNotifications.map(n => n._id.toString()),
                notFoundIds,
            };
        } catch (error) {
            throw error instanceof HttpException
                ? error
                : new HttpException(
                    status.InternalServerError,
                    t('General.errorDeleting', { field: t('Notification.notification') }))

        }
    }

    public async deleteAllNotifications(t: TFunction, userId: string): Promise<any> {
        try {
            const foundNotifications = await NotificationModel.find({ to: userId }).lean();

            if (foundNotifications.length === 0) {
                throw new HttpException(
                    status.NotFound,
                    t('General.not_found', { field: t('Notification.notification') }));

            }

            const deletionResult = await NotificationModel.deleteMany({ to: userId });

            const io = this.getIO();
            io.to(`user_${userId}`).emit('all-notifications-deleted', { deletedIds: foundNotifications.map(n => n._id.toString()) });

            return {
                deletedCount: deletionResult.deletedCount,
                deletedIds: foundNotifications.map(n => n._id.toString()),
            };
        } catch (error) {
            throw error instanceof HttpException
                ? error
                : new HttpException(
                    status.InternalServerError,
                    t('General.not_found', { field: t('Notification.notification') }));

        }
    }
}